import { useState, useCallback, useEffect } from 'react';
import axiosInstance from 'api/axiosInstance';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { Case } from 'containers/pages/cases/types';

const TAB_STORAGE_PREFIX = 'caseDetail_tab_';

function useCaseDetail() {
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [currentCaseId, setCurrentCaseId] = useState<number | null>(null);
  const { notifyError } = useNotificationProvider();

  const fetchCase = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    setCurrentCaseId(id);
    try {
      const response = await axiosInstance.get(`/api/cantrackv3/ReadCase?id=${id}`);
      if (!response || !response.data) {
        throw new Error('Failed to fetch case');
      }
      setCaseData(response.data);

      const savedTab = localStorage.getItem(`${TAB_STORAGE_PREFIX}${id}`);
      if (savedTab !== null) {
        setActiveTab(Number(savedTab));
      } else {
        setActiveTab(0);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch case';
      console.error('[CaseDetail] Error fetching case:', err);
      setError(message);
      notifyError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [notifyError]);

  const refetch = useCallback(async () => {
    if (currentCaseId !== null) {
      await fetchCase(currentCaseId);
    }
  }, [currentCaseId, fetchCase]);

  const handleSetActiveTab = useCallback((tab: number) => {
    setActiveTab(tab);
    if (currentCaseId !== null) {
      localStorage.setItem(`${TAB_STORAGE_PREFIX}${currentCaseId}`, String(tab));
    }
  }, [currentCaseId]);

  useEffect(() => {
    return () => {
      setCaseData(null);
      setError(null);
      setActiveTab(0);
    };
  }, []);

  const state = { caseData, loading, error, activeTab };
  const handler = { fetchCase, refetch, setActiveTab: handleSetActiveTab };

  return { state, handler };
}

export default useCaseDetail;
